/* eslint-disable react-hooks/exhaustive-deps */
import * as React from "react";
import styled, { withTheme } from "styled-components/macro";
import { darken } from "polished";
import { Search as SearchIcon } from "react-feather";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "../../CustomAxios";

import {
  Grid,
  InputBase,
  AppBar as MuiAppBar,
  IconButton as MuiIconButton,
  Toolbar,
} from "@mui/material";

import { Menu as MenuIcon } from "@mui/icons-material";

import NavbarUserDropdown from "./NavbarUserDropdown";
import { connect } from "react-redux";
import { loadUser } from "../../actions/AuthActions";
import { useNavigate } from "react-router-dom";

const AppBar = styled(MuiAppBar)`
  background: ${(props) => props.theme.header.background};
  color: ${(props) => props.theme.header.color};
`;

const IconButton = styled(MuiIconButton)`
  svg {
    width: 22px;
    height: 22px;
  }
`;

const Search = styled.div`
  border-radius: 2px;
  background-color: ${(props) => props.theme.header.background};
  display: none;
  position: relative;
  width: 100%;
  &:hover {
    background-color: ${(props) => darken(0.05, props.theme.header.background)};
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    display: block;
  }
`;

const SearchIconWrapper = styled.div`
  width: 50px;
  height: 100%;
  position: absolute;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 22px;
    height: 22px;
  }
`;

const Overlay = styled.div`
  position: absolute;
  z-index: 100;
  background: #fff;
  width: 100%;
  max-height: 250px;
  box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.1);
  overflow: scroll;
  margin-top: 10px;
  padding-bottom: 20px;
  ::-webkit-scrollbar {
    display: none;
  }
`;

const Job = styled.div`
  padding: 1rem;
  cursor: pointer;
  &:hover {
    background-color: #eee;
  }
`;

const Input = styled(InputBase)`
  color: inherit;
  width: 100%;

  > input {
    color: ${(props) => props.theme.header.search.color};
    padding-top: ${(props) => props.theme.spacing(2.5)};
    padding-right: ${(props) => props.theme.spacing(2.5)};
    padding-bottom: ${(props) => props.theme.spacing(2.5)};
    padding-left: ${(props) => props.theme.spacing(12)};
    width: 220px;
  }
`;

function useOutsideAlerter(ref, setJobs) {
  React.useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setJobs([]);
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref]);
}

const Navbar = ({ onDrawerToggle, loadUser, auths }) => {
  const navigate = useNavigate();
  const [jobs, setJobs] = React.useState([]);

  const [selectedJob, setSelectedJob] = React.useState("");
  const [searchKeyword, setSearchKeyword] = React.useState("");
  const [forbidden, setForbidden] = React.useState(true);
  const [loading, setLoading] = React.useState(false);

  const wrapperRef = React.useRef(null);
  useOutsideAlerter(wrapperRef, setJobs);

  const getJobs = async () => {
    const trimmedKeyword = searchKeyword.trim();

    if (!trimmedKeyword) {
      setJobs([]); // Clear results if no keyword
      return;
    }

    try {
      setLoading(true);

      const userData = JSON.parse(localStorage.getItem("AuthData"));
      const config = {
        headers: {
          Authorization: "Bearer " + userData.access_token,
        },
      };

      let endpoint = "";

      if (userData?.type === "ap_user" || userData?.type === "super_admin") {
        endpoint = `/api/v1/${userData?.type}/jobs?search_keyword=${trimmedKeyword}&is_table=true`;
      } else if (auths.user?.customer_company?._id) {
        const companyId = auths.user.customer_company._id;
        endpoint = `/api/v1/${userData?.type}/${companyId}/jobs?search_keyword=${trimmedKeyword}&is_table=true`;
      } else {
        return;
      }

      const { data } = await axios.get(endpoint, config);

      if (data.success) {
        setJobs(data.data);
      }
    } catch (e) {
      console.error("Error fetching jobs", e);
    } finally {
      setLoading(false);
    }
  };



  React.useEffect(() => {
    (async () => {
      if (!auths.token) {
        navigate("/login");
      } else {
        const data = await loadUser();

        if (!data.success) {
          navigate("/login");
        } else {
          setForbidden(false); 
        }
      }
    })();
  }, []);

  // React.useEffect(() => {
  //   getJobs();
  // }, [auths]);

  React.useEffect(() => {
    if (selectedJob) {
      navigate(`/dashboard/jobs/job-details/${selectedJob._id}`);
      setSearchKeyword("");
      // setFilteredJobs(jobs);
    }
  }, [selectedJob]);

  const searchOnEnter = (e) => {
    if (e.key === "Enter") getJobs();
  };

  const { t } = useTranslation();
  return (
    <React.Fragment>
      <AppBar position="sticky" elevation={0}>
        <Toolbar ref={wrapperRef}>
          <Grid container alignItems="center">
            <Grid item sx={{ display: { xs: "block", md: "none" } }}>
              <IconButton
                color="inherit"
                aria-label="Open drawer"
                onClick={onDrawerToggle}
                size="large"
              >
                <MenuIcon />
              </IconButton>
            </Grid>
            <Grid
              item
              style={{
                width: "calc(100% - 46px)",
              }}
            >
              {!forbidden && (
                <>
                  <Search>
                    <SearchIconWrapper>
                      <SearchIcon />
                    </SearchIconWrapper>
                    <Input
                      placeholder={t("Search Jobs (By Job Name/Job ID)")}
                      value={searchKeyword}
                      onChange={(e) => {
                        setSearchKeyword(e.target.value);
                        setJobs([]);
                      }}
                      fullWidth
                      variant="outlined"
                      onKeyDown={searchOnEnter}
                    />
                  </Search>

                  {!loading &&
                    searchKeyword &&
                    searchKeyword.trim() !== "" &&
                    jobs &&
                    jobs.length > 0 && (
                      <Overlay>
                        {jobs.map((job) => {
                          return (
                            <Job
                              key={job._id}
                              onClick={() => setSelectedJob(job)}
                            >
                              {job.job_id} - {job.name}
                            </Job>
                          );
                        })}
                      </Overlay>
                    )}
                </>
              )}
            </Grid>
            <Grid item>
              <NavbarUserDropdown />
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
    </React.Fragment>
  );
};

Navbar.propTypes = {
  auths: PropTypes.object.isRequired,
  loadUser: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  auths: state.auths,
});

export default connect(mapStateToProps, { loadUser })(withTheme(Navbar));
